/**
 * Master Data Contact Store
 * Zustand store for standalone contact state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  MasterDataContactListItem,
  MasterDataContactDetail,
  ContactType,
} from '@/types/api/masterDataContact';

/**
 * Contact filter state
 */
export interface MasterDataContactFilters {
  search: string;
  typeId: ContactType; // 1=Vendor, 2=Customer
  isPrimary: boolean | null;
  isActive: boolean | null;
}

/**
 * Contact pagination state
 */
export interface MasterDataContactPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Contact sort state
 */
export interface MasterDataContactSort {
  sortBy: number | null;
  sortOrder: 0 | 1; // 0=ASC, 1=DESC
}

/**
 * Contact store state
 */
export interface MasterDataContactState {
  // Data
  contacts: MasterDataContactListItem[];
  currentContact: MasterDataContactDetail | null;

  // Pagination
  pagination: MasterDataContactPagination;

  // Filters
  filters: MasterDataContactFilters;

  // Sorting
  sort: MasterDataContactSort;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Actions - Data
  setContacts: (contacts: MasterDataContactListItem[]) => void;
  setCurrentContact: (contact: MasterDataContactDetail | null) => void;
  addContact: (contact: MasterDataContactListItem) => void;
  updateContactInList: (contactId: number, updates: Partial<MasterDataContactListItem>) => void;
  removeContact: (contactId: number) => void;

  // Actions - Pagination
  setPagination: (pagination: Partial<MasterDataContactPagination>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;

  // Actions - Filters
  setFilters: (filters: Partial<MasterDataContactFilters>) => void;
  setSearch: (search: string) => void;
  setTypeId: (typeId: ContactType) => void;
  setIsPrimary: (isPrimary: boolean | null) => void;
  setIsActive: (isActive: boolean | null) => void;
  resetFilters: () => void;

  // Actions - Sorting
  setSort: (sort: Partial<MasterDataContactSort>) => void;
  setSortBy: (sortBy: number | null) => void;
  setSortOrder: (sortOrder: 0 | 1) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  contacts: [],
  currentContact: null,
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    typeId: 1 as ContactType, // Default to Vendor tab
    isPrimary: null,
    isActive: true, // Default to showing only active contacts
  },
  sort: {
    sortBy: null,
    sortOrder: 0 as 0 | 1,
  },
  isLoading: false,
  error: null,
};

/**
 * Master Data Contact Zustand store
 */
export const useMasterDataContactStore = create<MasterDataContactState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        // Data actions
        setContacts: (contacts) => set({ contacts }, false, 'masterDataContacts/setContacts'),

        setCurrentContact: (contact) =>
          set({ currentContact: contact }, false, 'masterDataContacts/setCurrentContact'),

        addContact: (contact) =>
          set(
            (state) => ({ contacts: [contact, ...state.contacts] }),
            false,
            'masterDataContacts/addContact'
          ),

        updateContactInList: (contactId, updates) =>
          set(
            (state) => ({
              contacts: state.contacts.map((c) =>
                c.contactId === contactId ? { ...c, ...updates } : c
              ),
            }),
            false,
            'masterDataContacts/updateContactInList'
          ),

        removeContact: (contactId) =>
          set(
            (state) => ({
              contacts: state.contacts.filter((c) => c.contactId !== contactId),
            }),
            false,
            'masterDataContacts/removeContact'
          ),

        // Pagination actions
        setPagination: (pagination) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, ...pagination },
            }),
            false,
            'masterDataContacts/setPagination'
          ),

        setPage: (page) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, page },
            }),
            false,
            'masterDataContacts/setPage'
          ),

        setPageSize: (pageSize) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, pageSize, page: 1 },
            }),
            false,
            'masterDataContacts/setPageSize'
          ),

        // Filter actions
        setFilters: (filters) =>
          set(
            (state) => ({
              filters: { ...state.filters, ...filters },
              pagination: { ...state.pagination, page: 1 }, // Reset to page 1
            }),
            false,
            'masterDataContacts/setFilters'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'masterDataContacts/setSearch'
          ),

        setTypeId: (typeId) =>
          set(
            (state) => ({
              filters: { ...state.filters, typeId },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'masterDataContacts/setTypeId'
          ),

        setIsPrimary: (isPrimary) =>
          set(
            (state) => ({
              filters: { ...state.filters, isPrimary },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'masterDataContacts/setIsPrimary'
          ),

        setIsActive: (isActive) =>
          set(
            (state) => ({
              filters: { ...state.filters, isActive },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'masterDataContacts/setIsActive'
          ),

        resetFilters: () =>
          set(
            (state) => ({
              filters: { ...initialState.filters, typeId: state.filters.typeId },
              pagination: { ...initialState.pagination },
            }),
            false,
            'masterDataContacts/resetFilters'
          ),

        // Sort actions
        setSort: (sort) =>
          set(
            (state) => ({
              sort: { ...state.sort, ...sort },
            }),
            false,
            'masterDataContacts/setSort'
          ),

        setSortBy: (sortBy) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortBy },
            }),
            false,
            'masterDataContacts/setSortBy'
          ),

        setSortOrder: (sortOrder) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortOrder },
            }),
            false,
            'masterDataContacts/setSortOrder'
          ),

        // UI state actions
        setLoading: (isLoading) => set({ isLoading }, false, 'masterDataContacts/setLoading'),

        setError: (error) => set({ error }, false, 'masterDataContacts/setError'),

        // Reset action
        reset: () => set(initialState, false, 'masterDataContacts/reset'),
      }),
      {
        name: 'master-data-contact-storage',
        // Only persist filters, pagination settings, and sort
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
          sort: state.sort,
        }),
        // Merge persisted state with initial state, ensuring isActive defaults to true
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<MasterDataContactState> | undefined;
          return {
            ...currentState,
            ...persisted,
            filters: {
              ...currentState.filters,
              ...persisted?.filters,
              // Default isActive to true only if strictly undefined (preserve explicit null for "show all")
              isActive: persisted?.filters?.isActive === undefined ? true : persisted.filters.isActive,
            },
          };
        },
      }
    ),
    { name: 'MasterDataContactStore' }
  )
);

/**
 * Selectors for common queries
 */

// Get filter query parameters for API
export const useMasterDataContactQueryParams = () => {
  const { filters, pagination, sort } = useMasterDataContactStore();

  // Convert isPrimary boolean to API format: 1=primary, 2=general, undefined=all
  let isPrimaryParam: number | undefined;
  if (filters.isPrimary === true) {
    isPrimaryParam = 1;
  } else if (filters.isPrimary === false) {
    isPrimaryParam = 2;
  } else {
    isPrimaryParam = undefined; // null means all
  }

  // Convert isActive boolean to API format: 1=active, 2=inactive, undefined=all
  let isActiveParam: number | undefined;
  if (filters.isActive === true) {
    isActiveParam = 1;
  } else if (filters.isActive === false) {
    isActiveParam = 2;
  } else {
    isActiveParam = undefined; // null means all
  }

  return {
    page: pagination.page,
    pageSize: pagination.pageSize,
    search: filters.search || undefined,
    typeId: filters.typeId,
    isPrimary: isPrimaryParam,
    isActive: isActiveParam,
    sortBy: sort.sortBy ?? undefined,
    sortOrder: sort.sortOrder,
  };
};

// Check if filters are applied (excluding typeId since it's always present)
export const useHasActiveMasterDataContactFilters = () => {
  const { filters } = useMasterDataContactStore();

  return (
    filters.search !== '' || filters.isPrimary !== null || filters.isActive !== null
  );
};

// Get current tab type
export const useCurrentContactType = () => {
  const { filters } = useMasterDataContactStore();
  return filters.typeId;
};
